import { Pipe, PipeTransform } from '@angular/core';
import {
  IQBoostViewStore,
  PiiAnonymizationService,
} from '@maximizer/ai/data-access';
import { escapeHtml } from '@maximizer/shared/util';
import { AiMarkupUtils } from '../classes/ai-markup-utils';
import { TranslateService } from '@ngx-translate/core';

@Pipe({ name: 'deAnonymizer' })
export class DeAnonymizerPipe implements PipeTransform {
  private readonly notSharedToAIText: string;
  private readonly anonymizedText: string;

  constructor(
    private readonly store: IQBoostViewStore,
    private readonly translate: TranslateService,
    private readonly piiAnonymization: PiiAnonymizationService,
  ) {
    this.notSharedToAIText = this.translate.instant(
      'iqboost.anonymization.notSharedToAI',
    );
    this.anonymizedText = this.translate.instant(
      'iqboost.anonymization.anonymized',
    );
  }

  transform(value?: string): string {
    if (!value) return '';

    // Server wraps PII as <anonymized value="…"/>; legacy <TYPE#> masks are handled below.
    let html = AiMarkupUtils.replaceAnonymizedTags(value, (pii) =>
      this.buildAnonymizedBadge(pii),
    );

    if (
      this.store.dataProcessingFeatureGroup().user.dataProcessingFeatures
        .anonymized ||
      this.store.dataProcessingFeatureGroup().admin.dataProcessingFeatures
        .anonymized
    ) {
      html = this.anonymizeHtml(html);
    } else {
      html = this.deanonymizeHtml(html);
    }

    return html;
  }

  /** `text` is plain PII text, so escape it before it lands in the badge markup. */
  private getIconTextHtml(icon: string, text: string): string {
    return `<i class="fa-icons ${icon} icon-14 text-neutral-70 pr-1"></i>${escapeHtml(text)}`;
  }

  /** `value` is the decoded PII; it also rides along as an attribute so the badge stays reversible. */
  private buildAnonymizedBadge(value: string): string {
    return `<span
        class="inline-flex items-center bg-transparent rounded p-0.5 m-[1px] border border-neutral-30 font-normal text-black"
        style="line-height:calc(1em + 0.5rem);"
        title="${escapeHtml(this.notSharedToAIText)}"
        data-anonymized-value="${escapeHtml(value)}"
      >
        ${this.getIconTextHtml('icon-lock-keyhole', value || this.anonymizedText)}
      </span>`;
  }

  private anonymizeHtml(html: string): string {
    return PiiAnonymizationService.processPiiMaskTokensInHtml(
      html,
      (type, number) => {
        const mask = `${type}${number}`;
        const original = this.piiAnonymization.deAnonymize(mask);
        const displayText = original || this.anonymizedText;

        return `<span
        class="inline-flex items-center bg-transparent rounded p-0.5 m-[1px] border border-neutral-30 font-normal text-black" 
        style="line-height:calc(1em + 0.5rem);"
        title="${escapeHtml(this.notSharedToAIText)}"
        data-type="${type.toLowerCase()}"
        data-number="${number.toLowerCase()}"
      >
        ${this.getIconTextHtml('icon-lock-keyhole', displayText)}
      </span>`;
      },
    );
  }

  private deanonymizeHtml(html: string): string {
    return this.piiAnonymization.deAnonymizeHtml(html);
  }
}
